/**
 * AccountPanel — who's logged in + cloud save controls.
 *
 * Shows the session e-mail, when the save last reached the cloud, a manual
 * "Sincronizar agora" and "Sair da conta" (logout → back to the login gate).
 * Built for the friends-test phase where people switch accounts on the same
 * machine.
 *
 * @module ui/panels/AccountPanel
 */

import { BasePanel } from './BasePanel.js';
import { getSession, pushSave, lastPushAt, logout } from '../auth.js';
import { confirmModal } from '../helpers/confirmModal.js';

function agoLabel(ts) {
  if (!ts) return 'ainda não sincronizado nesta sessão';
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (s < 5) return 'agora mesmo';
  if (s < 60) return `há ${s}s`;
  const m = Math.floor(s / 60);
  return m < 60 ? `há ${m}min` : `há ${Math.floor(m / 60)}h`;
}

export class AccountPanel extends BasePanel {
  constructor(opts) {
    super({ name: 'account', title: 'CONTA', state: opts.state, data: opts.data, hooks: opts.hooks });
    this._syncing = false;
  }

  render() {
    const session = getSession();
    if (!session) {
      this.body.innerHTML = '<p class="pd-empty">Nenhuma conta conectada.</p>';
      return;
    }

    this.body.innerHTML = `
      <div style="text-align:center;padding:6px 4px 2px">
        <div style="font-size:2rem;margin-bottom:6px">🛡️</div>
        <div style="font-size:13px;font-weight:800;color:var(--pd-chrome-text)">${session.email}</div>
        <div style="font-size:10px;color:var(--pd-muted);margin-top:2px">sessão ativa por 30 dias</div>
      </div>

      <div class="pd-section-title" style="margin-top:14px">Save na nuvem</div>
      <div style="display:flex;align-items:center;gap:8px;padding:10px;border:1px solid var(--pd-border);border-radius:8px;background:var(--pd-surface)">
        <span style="font-size:15px">☁️</span>
        <div style="flex:1">
          <div style="font-size:11px;color:var(--pd-text)">Último envio: <strong class="pd-acc-sync-when">${agoLabel(lastPushAt())}</strong></div>
          <div style="font-size:9px;color:var(--pd-muted)">automático a cada 1min e ao sair da aba</div>
        </div>
        <button class="pd-btn pd-btn-secondary pd-acc-sync" style="font-size:10px;padding:6px 10px" ${this._syncing ? 'disabled' : ''}>
          ${this._syncing ? '...' : '↻ Sincronizar'}
        </button>
      </div>

      <div class="pd-section-title" style="margin-top:14px">Sessão</div>
      <div style="display:flex;gap:8px;justify-content:center;margin-top:4px">
        <button class="pd-btn pd-btn-secondary pd-acc-logout" style="font-size:11px;padding:8px 18px">⏏ Sair da conta</button>
      </div>
      <p style="font-size:9px;color:var(--pd-muted);text-align:center;margin-top:8px;line-height:1.5">
        Seu progresso fica salvo na nuvem — ao entrar de novo (em qualquer dispositivo), ele volta.
      </p>`;

    this.body.querySelector('.pd-acc-sync')?.addEventListener('click', async () => {
      this._syncing = true;
      this.render();
      const ok = await pushSave();
      this._syncing = false;
      this.render();
      if (!ok) {
        const el = this.body.querySelector('.pd-acc-sync-when');
        if (el) { el.textContent = 'falhou — tente de novo'; el.style.color = '#ff8a7a'; }
      }
    });

    this.body.querySelector('.pd-acc-logout')?.addEventListener('click', async () => {
      const ok = await confirmModal(this.root.ownerDocument, {
        icon: '⏏',
        title: 'Sair da conta?',
        message: 'Seu progresso já está salvo na nuvem. A página vai recarregar na tela de login.',
        confirmLabel: 'Sair',
      });
      if (!ok) return;
      await pushSave(); // last sync so nothing since the interval is lost
      logout();
    });
  }
}
